import React, {useMemo, useState} from "react";
import { isNonEmptyArray, isNonEmptyString, isFunction } from "@cayuse/shared-react.utils.type-checks";
import { useAdminTeam } from "hooks";
import AssignSelected from "components/AssignSelected";
import CommentDisplayModal from "components/CommentDisplayModal";
import { COLUMN_HEADER_KEYS } from "globals/constants";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import Table from "components/Table";
import TextButton from "components/TextButton";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";

import styles from "./styles.css";


const FormHistoryView = ({
	instance,
	dictionary,
	updateInstanceSelection,
	canReadNotes,
	canAssign,
	adminRole,
	formType,
	formIdKey,
	refreshInstance
}) => {
	const hideCurrentlyAssigned = formType === COLUMN_HEADER_KEYS.PROJECT || formType === COLUMN_HEADER_KEYS.SUBAWARD
		? true : false; //TODO: this is temporary, remove when implementing new history tab

	const history = [...(instance.history ?? [])].reverse();
	const hasHistory = isNonEmptyArray(history);

	const adminTeamMembers = !hideCurrentlyAssigned && instance.adminTeamMembers ? instance.adminTeamMembers : []; //TODO: remove this when implementing new history tab
	const adminTeam = useAdminTeam(adminTeamMembers, formType, instance[formIdKey]);
	const currentlyAssignedMembers = !hideCurrentlyAssigned ? adminTeam.members : []; //TODO: remove this when implementing new history tab
	const hasCurrentAssignments = isNonEmptyArray(currentlyAssignedMembers);

	// review comment modal state
	const [ commentVisible, setCommentVisible ] = useState(false);
	const [ commentTitle, setCommentTitle ] = useState("");
	const [ comment, setComment ] = useState("");

	// admin team assignment popup state
	const [ isAssignmentOpen, setIsAssignmentOpen ] = useState(false);
	const [ selectedInstance, setSelectedInstance ] = useState(updateInstanceSelection(instance));

	const onCommentOpen = (createdBy, comment) => {
		setCommentTitle("Status Comment from " + createdBy);
		setComment(comment);
		setCommentVisible(true);
	};

	const onCommentClose = () => {
		setCommentVisible(false);
	};

	const renderAssignedPerson = ({ personId, fullName, roles }) => {
		const nameDisplay = isNonEmptyString(fullName)
			? fullName
			: dictionary.unknownUser || "Unknown User";
		const roleName = roles[0]?.roleName;

		const roleDisplay = isNonEmptyString(roleName)
			? roleName
			: dictionary.unknownRole || "Unknown Role";

		return (
			<span key={personId} className={styles.assignedUser}>
				<span>{nameDisplay}</span> - <span className={styles.roleName}>{roleDisplay}</span>
			</span>
		);
	};

	const updateAdminTeamAssignments = ({ type, person, role }) => {
		let promise;

		if (type === "add") {
			adminTeam.addMember(person, role.name);
			promise = adminTeam.save()
				.then(() => setIsAssignmentOpen(false));
		} else if (type === "remove") {
			adminTeam.removeMember(person);
			promise = adminTeam.save();
		}

		if (promise && isFunction(refreshInstance)) {
			promise.then(() => {
				refreshInstance();
			});
		}
	};

	const assignSelectedContent = canAssign
		? (
			<>
				<span>(</span>
				<div className={styles.editAssignmentWrapper}>
					<AssignSelected
						adminTeamMembers={adminTeam.members}
						instanceFeedItemSelections={selectedInstance}
						editMembers={true}
						isOpen={isAssignmentOpen}
						onOpen={() => setIsAssignmentOpen(true)}
						onClose={() => setIsAssignmentOpen(false)}
						onTeamModification={updateAdminTeamAssignments}
						triggerType="link"
						triggerLabel={dictionary.editAssignmentLabel || "edit"}
						adminRole={adminRole}
					/>
				</div>
				<span>)</span>
			</>
		)
		: "";

	return (
		<div className={styles.formHistoryView}>
			{!hideCurrentlyAssigned ?
				<VerticalSpacing>
					<div className={styles.currentlyAssigned}>
						<span className={styles.label}>{dictionary.currentlyAssigned || "Currently Assigned"}:</span>

						<span className={styles.assignmentDisplay}>
							{hasCurrentAssignments
								? currentlyAssignedMembers.map(renderAssignedPerson)
								: <span className={styles.assignedUser}>{dictionary.currentlyAssignedNone || "None"}</span>
							}
						</span>

						{assignSelectedContent}

					</div>
				</VerticalSpacing>
				:
				""}

			<CommentDisplayModal
				title={commentTitle || dictionary.commentModal || "Status Comment"}
				comment={comment}
				open={commentVisible}
				onClose={onCommentClose}
			/>

			<div>
				<Table captionText="Form History">
					<Table.Header>
						<Table.Row>
							<Table.HeaderCell>{dictionary.columnHeaders?.action || "Action"}</Table.HeaderCell>
							<Table.HeaderCell>{dictionary.columnHeaders?.name || "Name"}</Table.HeaderCell>
							<Table.HeaderCell>{dictionary.columnHeaders?.date || "Date"}</Table.HeaderCell>
							{canReadNotes && <Table.HeaderCell>{dictionary.columnHeaders?.comment || "Comment"}</Table.HeaderCell>}
						</Table.Row>
					</Table.Header>

					<Table.Body>
						{hasHistory
							? (history.map(({ message, createdBy, createdOn, comment }, index) => (
								<Table.Row key={index}>
									<Table.Cell>{message}</Table.Cell>
									<Table.Cell>{createdBy}</Table.Cell>
									<Table.Cell>
										<FormattedDatetime date={createdOn} />
									</Table.Cell>

									{canReadNotes &&
                  <Table.Cell>
	                  {isNonEmptyString(comment) &&
	                  <TextButton appearAsLink onClick={() => onCommentOpen(createdBy, comment)}>
		                  {dictionary.reviewCommentLabel || "view"}
	                  </TextButton>
	                  }
                  </Table.Cell>
									}
								</Table.Row>
							)))
							: ""
						}
					</Table.Body>
				</Table>
			</div>
		</div>
	);
};

export default FormHistoryView;
